import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, MapPin, Pause, Play, RefreshCw, SlidersHorizontal, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { deviceConfig, deviceHeartbeat, deviceNext, getStoredToken } from "../lib/deviceApi";

const IDLE_MS = 4000;
const HEARTBEAT_MS = 30000;
const SS_NOTE_MS = 6500; // screensaver advisory shows briefly on entry, then auto-hides
const TV_INTERVALS = [5, 10, 20, 30, 60];

export function TvPlayer({ onUnlinked, onExit }) {
  const token = getStoredToken();
  const [photo, setPhoto] = useState(null);
  const [portrait, setPortrait] = useState(false);
  const [cfg, setCfg] = useState({ interval_seconds: 10, always_show_details: true, preference_version: 0 });
  const [speed, setSpeed] = useState(10); // local slideshow interval (initialised from device config)
  const [playing, setPlaying] = useState(true);
  const [active, setActive] = useState(true);
  const [empty, setEmpty] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [detailsToggle, setDetailsToggle] = useState(false);
  const [ssNote, setSsNote] = useState(true);          // transient screensaver advisory
  const [filtersHelp, setFiltersHelp] = useState(false);
  const [intervalOpen, setIntervalOpen] = useState(false);
  const [intervalIdx, setIntervalIdx] = useState(1);   // highlighted option in the interval menu

  // Any modal/menu that must suspend the normal control auto-hide timer.
  const menuOpen = intervalOpen || filtersHelp;

  // Client-side actual-viewing history (approved design: no server-side "previous").
  const historyRef = useRef([]);
  const posRef = useRef(-1);
  const slideRef = useRef(null);
  const idleRef = useRef(null);
  const wakeLockRef = useRef(null);
  const activeOptRef = useRef(null);

  const handleAuthErr = useCallback((r) => {
    if (r.status === 403) { onUnlinked("This TV was unlinked from your account. Link it again to continue."); return true; }
    if (r.status === 401) { onUnlinked("This TV's session expired. Link it again to continue."); return true; }
    return false;
  }, [onUnlinked]);

  const loadConfig = useCallback(async () => {
    const r = await deviceConfig(token);
    if (r.ok) {
      // /api/device/config nests always_show_details under `preferences`; flatten it onto
      // the top-level cfg surface used by showDetails (interval_seconds is already top-level).
      const data = { ...r.data, always_show_details: r.data.preferences?.always_show_details ?? false };
      setCfg(data);
      return data;
    }
    handleAuthErr(r);
    return null;
  }, [token, handleAuthErr]);

  // Keep local slideshow speed in step with the device config (phone-controlled).
  useEffect(() => { setSpeed(cfg.interval_seconds || 10); }, [cfg.interval_seconds]);

  const fetchNextFromServer = useCallback(async () => {
    let delay = 1000;
    for (let attempt = 0; attempt < 8; attempt += 1) {
      const r = await deviceNext(token, { variant: "auto" });
      if (r.ok) {
        setReconnecting(false);
        if (r.data.empty) { setEmpty(true); return null; }
        setEmpty(false);
        return r.data.photo;
      }
      if (handleAuthErr(r)) return null;
      setReconnecting(true);
      await new Promise((res) => { setTimeout(res, delay); });
      delay = Math.min(delay * 2, 15000);
    }
    return null;
  }, [token, handleAuthErr]);

  const decodeAndSet = useCallback(async (p) => {
    if (!p) return;
    try {
      const im = new window.Image();
      im.src = p.image_url;
      if (im.decode) await im.decode();
      setPortrait(im.naturalHeight > im.naturalWidth);
    } catch (_e) { setPortrait(false); }
    setPhoto(p);
  }, []);

  const next = useCallback(async () => {
    if (posRef.current < historyRef.current.length - 1) {
      posRef.current += 1;
      await decodeAndSet(historyRef.current[posRef.current]);
      return;
    }
    const p = await fetchNextFromServer();
    if (!p) return;
    historyRef.current.push(p);
    posRef.current = historyRef.current.length - 1;
    await decodeAndSet(p);
  }, [decodeAndSet, fetchNextFromServer]);

  const prev = useCallback(async () => {
    if (posRef.current <= 0) return;
    posRef.current -= 1;
    await decodeAndSet(historyRef.current[posRef.current]);
  }, [decodeAndSet]);

  const nextRef = useRef(next);
  useEffect(() => { nextRef.current = next; }, [next]);

  useEffect(() => {
    (async () => { await loadConfig(); await nextRef.current(); })();
  }, [loadConfig]);

  // Screensaver advisory: brief on entry, then auto-hide.
  useEffect(() => {
    const t = setTimeout(() => setSsNote(false), SS_NOTE_MS);
    return () => clearTimeout(t);
  }, []);

  // Slideshow timer — paused while a menu/overlay is open.
  useEffect(() => {
    if (slideRef.current) clearTimeout(slideRef.current);
    if (playing && photo && !empty && !menuOpen) {
      slideRef.current = setTimeout(() => nextRef.current(), (speed || 10) * 1000);
    }
    return () => { if (slideRef.current) clearTimeout(slideRef.current); };
  }, [playing, photo, empty, speed, menuOpen]);

  // Heartbeat: detect phone-side preference changes and rebuild silently.
  useEffect(() => {
    const id = setInterval(async () => {
      const r = await deviceHeartbeat(token);
      if (!r.ok) { handleAuthErr(r); return; }
      if (r.data.preference_version !== cfg.preference_version) {
        await loadConfig();
        historyRef.current = []; posRef.current = -1;
        await nextRef.current();
      }
    }, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [token, cfg.preference_version, loadConfig, handleAuthErr]);

  // Screen Wake Lock where supported (best-effort; see documented TV screensaver note).
  const requestWake = useCallback(async () => {
    try {
      if ("wakeLock" in navigator && !wakeLockRef.current) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
        wakeLockRef.current.addEventListener?.("release", () => { wakeLockRef.current = null; });
      }
    } catch (_e) { /* unsupported / denied */ }
  }, []);
  useEffect(() => {
    requestWake();
    const onVis = () => { if (document.visibilityState === "visible") requestWake(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      try { wakeLockRef.current?.release?.(); } catch (_e) { /* noop */ }
      wakeLockRef.current = null;
    };
  }, [requestWake]);

  // Reveal chrome; auto-hide after inactivity UNLESS a menu/overlay is open.
  const wake = useCallback(() => {
    setActive(true);
    if (idleRef.current) clearTimeout(idleRef.current);
    if (!menuOpen) idleRef.current = setTimeout(() => setActive(false), IDLE_MS);
  }, [menuOpen]);
  useEffect(() => { wake(); return () => { if (idleRef.current) clearTimeout(idleRef.current); }; }, [wake]);

  // Keep chrome pinned while a menu/overlay is open; resume auto-hide when it closes.
  useEffect(() => {
    if (menuOpen) { setActive(true); if (idleRef.current) clearTimeout(idleRef.current); }
    else { wake(); }
  }, [menuOpen, wake]);

  // Focus the highlighted interval option so D-pad/remote navigation works.
  useEffect(() => { if (intervalOpen) activeOptRef.current?.focus(); }, [intervalOpen, intervalIdx]);

  const goFullscreen = () => {
    const el = document.documentElement;
    if (!document.fullscreenElement) el.requestFullscreen?.().catch(() => {});
  };

  // Exit TV player mode and return to the paired-TV home. Never clears the wwd_ token.
  const exitToHome = useCallback(() => {
    try { document.exitFullscreen?.().catch(() => {}); } catch (_e) { /* noop */ }
    onExit?.();
  }, [onExit]);

  const openInterval = () => { setIntervalIdx(Math.max(0, TV_INTERVALS.indexOf(speed))); setIntervalOpen(true); };
  const chooseInterval = (val) => { setSpeed(val); setIntervalOpen(false); };

  // Remote / D-pad / keyboard controls.
  useEffect(() => {
    const onKey = (e) => {
      wake();
      if (filtersHelp) {
        if (["Escape", "Enter", " ", "GoBack", "BrowserBack"].includes(e.key)) { e.preventDefault(); setFiltersHelp(false); }
        return;
      }
      if (intervalOpen) {
        if (e.key === "ArrowDown") { e.preventDefault(); setIntervalIdx((i) => Math.min(TV_INTERVALS.length - 1, i + 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setIntervalIdx((i) => Math.max(0, i - 1)); }
        else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); chooseInterval(TV_INTERVALS[intervalIdx]); }
        else if (["Escape", "GoBack", "BrowserBack"].includes(e.key)) { e.preventDefault(); setIntervalOpen(false); }
        return;
      }
      // No overlay: arrows page photos; Enter/Space activate the focused control natively.
      switch (e.key) {
        case "ArrowRight": case "MediaTrackNext": case "MediaNext": next(); break;
        case "ArrowLeft": case "MediaTrackPrevious": case "MediaPrevious": prev(); break;
        case "MediaPlayPause": e.preventDefault(); setPlaying((p) => !p); break;
        case "Escape": case "GoBack": case "BrowserBack": exitToHome(); break;
        default: break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const loc = photo && [photo.location?.place, photo.location?.city, photo.location?.country].filter(Boolean).join(", ");
  const showDetails = photo && (cfg.always_show_details || detailsToggle);
  const stop = (e) => e.stopPropagation();

  return (
    <div className="tv-root tv-player" data-testid="tv-player"
         onMouseMove={wake} onClick={() => { goFullscreen(); wake(); }}
         style={{ cursor: active ? "default" : "none" }}>

      <AnimatePresence>
        {photo && (
          <motion.div key={photo.photo_id} className={`tv-frame ${portrait ? "is-portrait" : "is-landscape"}`}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
            {portrait && <img src={photo.image_url} alt="" aria-hidden="true" className="tv-bg" />}
            <img src={photo.image_url} alt={loc || "photograph"} className="tv-img" data-testid="tv-player-image" />
          </motion.div>
        )}
      </AnimatePresence>

      {!photo && !empty && <div className="tv-loading" data-testid="tv-player-loading">Finding a moment worth seeing…</div>}

      {reconnecting && (
        <div className="tv-reconnect" data-testid="tv-reconnecting">
          <RefreshCw size={15} className="spin" /> Reconnecting…
        </div>
      )}

      {empty && (
        <div className="tv-empty" data-testid="tv-player-empty">
          <div>
            <h2>Nothing to show just yet</h2>
            <p>Adjust your filters on your phone to see more of the world here.</p>
          </div>
        </div>
      )}

      {photo && (
        <>
          <div className="tv-scrim-top" />
          <div className="tv-scrim-bottom" />

          {/* Picture information — top-left, compact, auto-shown per always_show_details. */}
          {showDetails && (
            <div className="tv-meta-pill" data-testid="tv-player-meta">
              <MapPin size={18} style={{ marginTop: 2, flex: "0 0 auto" }} />
              <div>
                <div className="tv-meta-loc">{loc}</div>
                <div className="tv-meta-sub">{photo.date_display} · Shared by {photo.contributor_handle}</div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Chrome (controls) auto-hides on inactivity, like the established Viewer. */}
      <AnimatePresence>
        {photo && active && (
          <motion.div key="chrome"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>

            {/* Top-right: Exit */}
            <div className="tv-top-actions">
              <button className="tv-round" data-testid="tv-exit" aria-label="Exit to World Wide Wallpaper home"
                      onClick={(e) => { stop(e); exitToHome(); }}>
                <X size={22} />
              </button>
            </div>

            {/* Bottom-left: Change filters (TV MVP shows phone/web guidance) */}
            <div className="tv-bottom-left">
              <button className="tv-chip" data-testid="tv-change-filters"
                      onClick={(e) => { stop(e); setFiltersHelp(true); }}>
                <SlidersHorizontal size={16} /> Change filters
              </button>
            </div>

            {/* Bottom-centre: transport */}
            <div className="tv-controls" data-testid="tv-controls">
              <button className="tv-round" data-testid="tv-prev" aria-label="Previous photograph" onClick={(e) => { stop(e); prev(); }}><ChevronLeft size={26} /></button>
              <button className="tv-round play" data-testid="tv-playpause" autoFocus
                      aria-label={playing ? "Pause slideshow" : "Play slideshow"} onClick={(e) => { stop(e); setPlaying((p) => !p); }}>
                {playing ? <Pause size={32} /> : <Play size={32} />}
              </button>
              <button className="tv-round" data-testid="tv-next" aria-label="Next photograph" onClick={(e) => { stop(e); next(); }}><ChevronRight size={26} /></button>
            </div>

            {/* Bottom-right: interval selector (custom, D-pad friendly, stays open until choose/cancel) */}
            <div className="tv-bottom-right" data-testid="tv-interval-wrap">
              {intervalOpen && (
                <div className="tv-interval-menu" data-testid="tv-interval-menu" role="listbox" aria-label="Slideshow interval">
                  {TV_INTERVALS.map((s, i) => (
                    <button key={s} type="button" role="option" aria-selected={s === speed}
                            ref={i === intervalIdx ? activeOptRef : null}
                            className={`tv-interval-opt ${i === intervalIdx ? "is-active" : ""}`}
                            data-testid={`tv-interval-opt-${s}`}
                            onMouseEnter={() => setIntervalIdx(i)}
                            onClick={(e) => { stop(e); chooseInterval(s); }}>
                      <span className={`tv-interval-radio ${s === speed ? "on" : ""}`} />
                      {s} sec
                    </button>
                  ))}
                </div>
              )}
              <button className="tv-interval-btn" data-testid="tv-interval" aria-haspopup="listbox" aria-expanded={intervalOpen}
                      onClick={(e) => { stop(e); if (intervalOpen) setIntervalOpen(false); else openInterval(); }}>
                <span className="tv-interval-label">Interval</span>
                <span className="tv-interval-value" data-testid="tv-interval-value">{speed} sec</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Screensaver advisory — brief on entry, then auto-hidden. */}
      <AnimatePresence>
        {ssNote && (
          <motion.div className="tv-ss-note" data-testid="tv-screensaver-note"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.4 }}>
            Some TV browsers run a system screensaver we can't override. A native TV app (coming later) will address this.
          </motion.div>
        )}
      </AnimatePresence>

      {/* Change-filters guidance overlay (phone/web guidance for the TV MVP). */}
      <AnimatePresence>
        {filtersHelp && (
          <motion.div className="tv-filters-help" data-testid="tv-filters-help"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            onClick={() => setFiltersHelp(false)}>
            <div className="tv-filters-help-card" onClick={stop}>
              <h2>Change what you see here</h2>
              <p>
                Open World Wide Wallpaper on your phone or computer and adjust your filters there.
                This TV updates automatically — no typing needed on the television.
              </p>
              <button className="tv-btn" data-testid="tv-filters-help-close" autoFocus onClick={(e) => { stop(e); setFiltersHelp(false); }}>
                Got it
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
